import { Button, Modal, Select, Table } from 'antd';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { CloseModalSVG } from 'components/iconsSVG/close';
import { i18nContext } from 'context/i18n';
import { array, string } from 'prop-types';
import { useContext, useEffect, useRef, useState } from 'react';

import { IS_CAMPER, IS_NNORMAL } from 'utils/constants/system';
import { SOCKS_SIZE_ORDER, STANDARD_SIZE_GUIDE, COUNTRIES_USING_INCHES, DEFAULT_GUIDE_UNITS, UNIT_LABELS_MAPPER } from 'utils/constants/sizes';

import { dataLayerHandleEvent } from 'utils/dataLayers';
import { getMarket, getTarget } from 'utils/helpers';
import { columns } from './guide';
import { MeasureCamper, MeasureNnormal } from './measure';
import { getDomainValue } from 'utils/domain';
import { isBlank } from 'utils/StringUtils';

import styles from './style.module.css';

function SizesGuide({ guide, code, target, currentCountry, name, sizeGuideInfo }) {
  const { t, locale } = useContext(i18nContext);
  const market = getMarket(locale);

  const targets = guide.map((item) => item.targetId);
  const defaultTarget = targets.indexOf(target);

  const defaultTab = defaultTarget >= 0 ? defaultTarget : 0;

  const columnsToUse = useRef(columns);

  if (typeof guide[0]?.equivalences !== 'object') {
    // nnormal no es zapato
    const cols: any[] = [
      {
        title: '',
        dataIndex: 'part',
        key: 'part',
        render: (text) => t('ficha.producto', `size.apparel.part.${text}`, text),
      },
    ];
    const sizes = new Set(
      Object.values(guide[0].guideSizes)
        .flat()
        .filter((s: { size: string; sizeValue: string }) => !isBlank(s.size) && !isBlank(s.sizeValue))
        .map(({ size }) => size),
    );
    sizes.forEach((size) => {
      cols.push({
        title: size,
        dataIndex: size,
        key: size,
      });
    });
    columnsToUse.current = { baseColumns: cols };
  }

  let defaultUnits = null;

  if (COUNTRIES_USING_INCHES.includes(market)) {
    defaultUnits = 'in';
  } else {
    defaultUnits = market === 'KR' ? 'mm' : 'cm';
  }

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [units, setUnits] = useState(defaultUnits);
  const [sizeNode, setSizeNode] = useState('guideSizes');

  const defaultGuide = guide.find((item) => item.targetId === targets[defaultTarget]) ?? guide?.[0];
  let defaultCountryColumns = [];
  if (defaultGuide && defaultGuide[sizeNode]) {
    defaultCountryColumns = defaultGuide[sizeNode][Object.keys(defaultGuide[sizeNode])[0]]?.map((row) => row.country) ?? [];
  }
  const defaultNonStandard = defaultCountryColumns.filter((countryItem) => !STANDARD_SIZE_GUIDE.includes(countryItem));
  const defaultSelectedCountry = defaultNonStandard.includes(market) ? market : null;
  const [selectedCountry, setSelectedCountry] = useState(defaultSelectedCountry);

  const showModal = () => {
    setIsModalVisible(true);
    if (!defaultGuide) return;
    const targetValue = getTarget(defaultGuide.targetId, 2);
    dataLayerHandleEvent({
      event: 'item_size_guide',
      event_category: 'product detail',
      event_detail_1: 'open',
      event_detail_2: targetValue,
    });
    const eventGA4 = {
      event: 'shoe_size_guide',
      item_name: name,
      item_id: code,
    };
    dataLayerHandleEvent(eventGA4);
  };
  const handleCancel = () => {
    dataLayerHandleEvent({
      event: 'item_size_guide',
      event_category: 'product detail',
      event_detail_1: 'close',
      event_detail_2: '',
    });
    setIsModalVisible(false);
  };
  const onChangeTab = (key) => {
    const { targetId } = guide[key];
    const targetValue = getTarget(targetId, 2);

    dataLayerHandleEvent({
      event: 'item_size_guide',
      event_category: 'product detail',
      event_detail_1: targetValue,
      event_detail_2: '',
    });

    setActiveTab(key);
  };

  /**
   * Extra la talla en tipo float a partir de un string
   * La talla puede llegar como "23cm", "23cm / 9.1in" o "22.5cm / 8.9in"
   * Lo importante es el número de delante
   */
  const extractSizeFloat = (sizeStr) => {
    const rx = /^(\d\.?)+/g;
    const arr = rx.exec(sizeStr);
    try {
      return parseFloat(arr?.[0]);
    } catch (e) {
      console.error(e);
      return 0;
    }
  };

  /**
   * Ordena las tallas de menor a mayor
   */
  const sortGuideSizes = (a, b) => {
    const numA = extractSizeFloat(a);
    const numB = extractSizeFloat(b);
    if (numA === numB) {
      return 0;
    }
    return numA > numB ? 1 : -1;
  };

  useEffect(() => {
    // como antd mete estilos inline que nos afectan al overflow
    // lo tenemos que modificar
    // por suerte nos dará tiempo antes de que el user llegue a abrir el modal
    if (isModalVisible) {
      const tableContent = document.querySelectorAll('div.ant-table-content');
      tableContent.forEach((content) => {
        // @ts-ignore
        content.style.overflow = 'unset';
      });
    }
  }, [isModalVisible]);

  useEffect(() => {
    if (process.browser) {
      const existingColumns = columnsToUse.current.baseColumns;
      const newBaseColumns = [
        {
          ...existingColumns[0],
          key: `feet.length${UNIT_LABELS_MAPPER[units]}`,
          title: '',
          defaultValue: '***Foot length ()',
        },
        ...existingColumns.slice(1),
      ];
      columnsToUse.current = { baseColumns: newBaseColumns };
      setSizeNode(DEFAULT_GUIDE_UNITS.includes(units) ? 'guideSizes' : 'guideSizesAlt');
    }
  }, [units]);

  const tabButtons = guide.map((sizeItem, index) => (
    <Button
      className={styles.targetTab}
      onClick={() => {
        onChangeTab(index);
      }}
      // @ts-ignore antd
      type={activeTab === index ? 'primary' : 'secondary'}
      key={`tab-${index}-${sizeItem.targetId}`}
    >
      {sizeItem.target}
    </Button>
  ));

  const dataRowSortMethod = (a, b) => {
    const [formattedA, formattedB] = [a.replace(',', '.'), b.replace(',', '.')];
    const [parsedA, parsedB] = [
      parseFloat(formattedA.includes('-') ? formattedA.split('-')[0] : formattedA),
      parseFloat(formattedB.includes('-') ? formattedB.split('-')[0] : formattedB),
    ];

    if (Number.isNaN(parsedA)) {
      return 1;
    }

    if (Number.isNaN(parsedB)) {
      return 1;
    }

    if (parsedA === parsedB) {
      return 0;
    }

    return parsedA > parsedB ? 1 : -1;
  };

  return (
    <>
      <div className={styles.sizeGuideIconWrapper}>
        {getDomainValue({
          camper: <img onClick={showModal} src="/assets-new/sizeguide.png" alt="Size Guide Icon" width={20} height={20} />,
        })}
        <span
          onClick={showModal}
          className={getDomainValue({
            camper: styles.sizesGuideSpan,
            nnormal: styles.sizesGuideSpan,
            camperlab: 'text xs uppercase underline',
          })}
        >
          {t('ficha.producto', 'size.fit')}
        </span>
      </div>
      {/* @ts-ignore antd */}
      <Modal
        title={null}
        visible={isModalVisible}
        footer={null}
        width="80%"
        onCancel={handleCancel}
        closeIcon={<CloseModalSVG />}
        wrapClassName={`${styles.modalWrapper} `}
      >
        <div>
          {getDomainValue({
            camper: (
              <>
                <div className="[@media(min-width:1400px)]:hidden">
                  <Select className={`${styles.targetSelector} `} onChange={onChangeTab} suffixIcon={<ChevronSVG />} defaultValue={guide[defaultTab].target}>
                    {guide.map((sizeItem, index) => (
                      <Select.Option key={`target-option-${sizeItem.targetId}`} value={index}>
                        {sizeItem.target}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div className="[@media(max-width:1400px)]:hidden">
                  <div className={styles.tabButtons}>{tabButtons}</div>
                </div>
                <div className={styles.measureToggle} onClick={() => setUnits((current) => (current === 'cm' ? 'in' : 'cm'))}>
                  <span>{t('ficha.producto', 'size.units.cm', 'CM')}</span>

                  <div className={styles.toggleOval}>
                    <button className={`${styles.toggleEllipse} ${units === 'cm' ? styles.ellipseLeft : styles.ellipseRight}`} />
                  </div>

                  <span>{t('ficha.producto', 'size.units.in', 'IN')}</span>
                </div>
              </>
            ),
            nnormal: (
              <div className={styles.sizesGuideHeader}>
                <p className={styles.sizesGuideTitle}>{t('ficha.producto', 'size.guide.title', 'Size guide')}</p>
                <div className={styles.tabContainer}>{tabButtons}</div>
              </div>
            ),
          })}

          <div className={styles.customTab}>
            <div className={styles.tableAndMeasureContainer}>
              {guide.map((sizeGuide, index) => {
                let tableColumns = null;
                let dataSourceRows = null;
                if (sizeGuide.equivalences) {
                  const countryColumns = sizeGuide[sizeNode][Object.keys(sizeGuide[sizeNode])[0]]?.map((row) => row.country) ?? [];

                  columnsToUse.current.baseColumns.forEach((column) => {
                    if (column && column.title === '') {
                      const { key, defaultValue } = column;
                      const translatedTitle = t('ficha.producto', key, defaultValue);
                      column.title = translatedTitle;
                    }
                  });

                  const nonStandardCountries = countryColumns.filter((countryItem) => !STANDARD_SIZE_GUIDE.includes(countryItem));

                  const countrySelector = (
                    <Select
                      //@ts-ignore antd!
                      autoComplete="none"
                      showSearch
                      className={styles.countrySelector}
                      placeholder={t('modal.idiomas', 'select.country', 'Select a country')}
                      value={selectedCountry}
                      allowClear={false}
                      suffixIcon={<ChevronSVG />}
                      onChange={(item) => setSelectedCountry(item)}
                    >
                      {nonStandardCountries.map((item, countryIndex) => (
                        <Select.Option key={`non-standard-country-${countryIndex}`} value={item}>
                          {item}
                        </Select.Option>
                      ))}
                    </Select>
                  );

                  tableColumns = [
                    ...columnsToUse.current.baseColumns,
                    ...countryColumns
                      .filter((countryItem) => STANDARD_SIZE_GUIDE.includes(countryItem))
                      .map((country) => ({
                        title: country === currentCountry ? <div>{country}</div> : country,
                        dataIndex: country,
                        key: country,
                      })),
                    {
                      title: countrySelector,
                      dataIndex: selectedCountry === null ? 'country-select' : selectedCountry,
                      key: 'country-select-column',
                      render: (value) => {
                        const obj = {
                          children: value,
                          props: {
                            className: 'selected-country-cell',
                          },
                        };
                        return obj;
                      },
                    },
                  ];
                  let currentSockSize = null;
                  let assignedCurrentSockSize = false;
                  let currentSockSizeIndex = null;
                  let lastCurrentSockSize = null;
                  if (sizeGuide?.socksSizes && Object.keys(sizeGuide.socksSizes).length) {
                    tableColumns.push({
                      title: t('ficha.producto', 'socks'),
                      dataIndex: 'sockSize',
                      key: 'sockSize',
                      render: (value, row) => {
                        const obj: any = {
                          children: value,
                          props: {
                            className: 'socks',
                          },
                        };
                        if (row.sockSize) {
                          obj.props.rowSpan = row.sockSizeSpan;
                        } else {
                          obj.props.rowSpan = 0;
                        }
                        return obj;
                      },
                    });
                    currentSockSize = SOCKS_SIZE_ORDER.find((size) => Object.keys(sizeGuide.socksSizes).includes(size));
                    currentSockSizeIndex = SOCKS_SIZE_ORDER.indexOf(currentSockSize);
                    lastCurrentSockSize = sizeGuide.socksSizes[currentSockSize][sizeGuide.socksSizes[currentSockSize].length - 1];
                  }
                  dataSourceRows = Object.keys(sizeGuide[sizeNode])
                    .sort(IS_NNORMAL ? sortGuideSizes : dataRowSortMethod)
                    .map((footLength) => {
                      const footLengthToUse = market === 'KR' ? Number(footLength) / 10 : footLength;
                      const filteredRow = countryColumns.map((country) => sizeGuide[sizeNode][footLength].filter((row) => row.country === country)).flat();
                      const countrysKeyValueObject = filteredRow.reduce((r, e) => {
                        r[e.country] = e.size;
                        return r;
                      }, {});

                      const row = {
                        key: footLength,
                        footLength,
                        equivalent: footLengthToUse in sizeGuide.equivalences ? sizeGuide.equivalences[footLengthToUse] : '',
                        ...countrysKeyValueObject,
                      };

                      if (sizeGuide.socksSizes) {
                        if (!assignedCurrentSockSize) {
                          row.sockSizeSpan = sizeGuide.socksSizes[currentSockSize].length;
                          row.sockSize = currentSockSize;
                          assignedCurrentSockSize = true;
                        }
                        const isLastIndexSockSize = row.EU === lastCurrentSockSize;
                        if (isLastIndexSockSize) {
                          currentSockSizeIndex = currentSockSizeIndex + 1;
                          currentSockSize = SOCKS_SIZE_ORDER[currentSockSizeIndex];
                          if (SOCKS_SIZE_ORDER[currentSockSizeIndex]) {
                            lastCurrentSockSize = sizeGuide.socksSizes[currentSockSize][sizeGuide.socksSizes[currentSockSize].length - 1];
                            assignedCurrentSockSize = false;
                          }
                        }
                      }
                      if (row[currentCountry]) {
                        row[currentCountry] = <div>{row[currentCountry]}</div>;
                      }
                      return row;
                    });
                } else {
                  dataSourceRows = Object.entries(guide[0].guideSizes).map(([part, partSizes]: any) => ({
                    part,
                    ...Object.fromEntries(partSizes.map(({ size, sizeValue }) => [size, sizeValue])),
                  }));
                  tableColumns = columnsToUse.current.baseColumns;
                }
                if (IS_CAMPER) {
                  dataSourceRows = dataSourceRows.map((row) => {
                    Object.keys(row).map((key) => {
                      let value: string | string[] = '';
                      if (typeof row[key] === 'string') {
                        value = row[key].split(' / ');
                        if (value.length === 2) {
                          row[key] = value[units === 'cm' ? 0 : 1];
                        }
                      }
                      return value;
                    });
                    return row;
                  });
                }
                return (
                  <Table
                    key={`sizesGuide-table-${sizeGuide.targetId}`}
                    size="small"
                    footer={undefined}
                    dataSource={dataSourceRows}
                    columns={tableColumns}
                    pagination={false}
                    className={`${styles.customTable} ${IS_CAMPER && guide[0].productType !== 'shoes' ? styles.clothsTable : ''} ${index === activeTab ? '' : 'escondido'}`}
                    scroll={{ x: true }}
                  />
                );
              })}
              {IS_CAMPER ?
                <MeasureCamper type={guide[0].productType} sizeGuideInfo={sizeGuideInfo} />
              : <MeasureNnormal type={guide[0].productType} target={target} targetId={targets[activeTab]} />}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

SizesGuide.propTypes = {
  guide: array,
  code: string,
  target: string,
  currentCountry: string,
  name: string,
  sizeGuideInfo: string,
};

export default SizesGuide;
